import React, { useState } from "react";
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Tooltip,
  Fade,
  Zoom,
  Box,
  alpha,
  useTheme,
} from "@mui/material";

export function ViewModeMenu({
  options = [],
  value,
  onChange,
  animationType = "fade",
  size = "medium",
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const open = Boolean(anchorEl);
  const theme = useTheme();

  const handleOpen = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleSelect = (val) => {
    onChange(val);
    handleClose();
  };

  const currentOption = options.find((opt) => opt.value === value);
  const nextOption = options.find((opt) => opt.value !== value);

  // Enhanced toggle function with animation
  const handleToggle = async () => {
    if (isAnimating || options.length !== 2) return;

    setIsAnimating(true);

    // Add delay based on animation type
    const delay = animationType === "flip" ? 150 : 100;
    setTimeout(() => {
      onChange(nextOption?.value);
      setIsAnimating(false);
    }, delay);
  };

  // Size mappings
  const sizeMap = {
    small: { iconSize: "small", padding: "6px" },
    medium: { iconSize: "small", padding: "8px" },
    large: { iconSize: "medium", padding: "10px" },
  };

  const { iconSize, padding } = sizeMap[size];

  const getIconStyles = () => {
    if (options.length !== 2) return {};

    switch (animationType) {
      case "fade":
        return {
          opacity: isAnimating ? 0 : 1,
          transition: "opacity 0.2s ease-in-out",
        };

      case "zoom":
        return {
          transform: isAnimating ? "scale(0.8)" : "scale(1)",
          opacity: isAnimating ? 0.5 : 1,
          transition: "all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)",
        };

      case "flip":
        return {
          transform: isAnimating ? "rotateY(90deg)" : "rotateY(0deg)",
          transition: "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        };

      case "slide":
        return {
          transform: isAnimating ? "translateX(10px)" : "translateX(0px)",
          opacity: isAnimating ? 0 : 1,
          transition: "all 0.2s ease-in-out",
        };

      default:
        return {};
    }
  };

  // Two options: Use simple toggle button
  if (options.length === 2) {
    return (
      <Tooltip
        title={`Switch to ${nextOption?.label || "other view"}`}
        placement='top'
      >
        <IconButton
          onClick={handleToggle}
          disabled={isAnimating}
          sx={{
            color: `${alpha(theme.palette.primary.main, 0.9)}`,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: `${alpha(theme.palette.primary.main, 0.4)}`,
            "&:hover": {
              bgcolor: "grey.100",
              borderColor: "grey.400",
            },
            transition: "all 0.2s ease-in-out",
          }}
        >
          {currentOption?.icon && <currentOption.icon fontSize={iconSize} />}
        </IconButton>
      </Tooltip>
    );
  }

  // More than two options: Use menu
  return (
    <>
      <Tooltip
        title={currentOption?.label || "Select view mode"}
        placement='top'
      >
        <IconButton
          onClick={handleOpen}
          sx={{
            padding,
            borderRadius: "8px",
            bgcolor: open
              ? alpha(theme.palette.primary.main, 0.15)
              : "transparent",
            border: "1px solid",
            borderColor: open
              ? alpha(theme.palette.primary.main, 0.3)
              : alpha(theme.palette.grey[300], 0.5),
            "&:hover": {
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              borderColor: alpha(theme.palette.primary.main, 0.2),
              transform: "translateY(-1px)",
            },
            transition: "all 0.2s ease-in-out",
          }}
        >
          {currentOption?.icon && <currentOption.icon fontSize={iconSize} />}
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        TransitionComponent={Fade}
        transitionDuration={200}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: theme.shadows[8],
            border: "1px solid",
            borderColor: alpha(theme.palette.grey[300], 0.2),
            mt: 1,
          },
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
      >
        {options.map((opt, index) => (
          <MenuItem
            key={opt.value}
            selected={value === opt.value}
            onClick={() => handleSelect(opt.value)}
            sx={{
              borderRadius: "8px",
              mx: 1,
              my: 0.5,
              "&:first-of-type": { mt: 1 },
              "&:last-of-type": { mb: 1 },
              "&.Mui-selected": {
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                "&:hover": {
                  bgcolor: alpha(theme.palette.primary.main, 0.15),
                },
              },
              transition: "all 0.15s ease-in-out",
            }}
          >
            {opt.icon && (
              <ListItemIcon sx={{ minWidth: "32px" }}>
                <Zoom in={true} timeout={100 + index * 50}>
                  <opt.icon fontSize='small' />
                </Zoom>
              </ListItemIcon>
            )}
            <ListItemText>{opt.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

// Usage example:
/*
<ViewModeMenu
  options={[
    { value: 'grid', label: 'Grid View', icon: GridViewIcon },
    { value: 'list', label: 'List View', icon: ViewListIcon }
  ]}
  value={viewMode}
  onChange={setViewMode}
  animationType="fade"
  size="medium"
/>
*/
